import React from 'react';
import { FaClipboardList, FaCheckCircle, FaMapMarkerAlt, FaWrench, FaPause, FaFlagCheckered, FaWallet, FaTimesCircle, FaBan, FaSpinner } from 'react-icons/fa';
import './StatusTracker.css';

const STATUS_STEPS = [
    { key: 'pending', label: 'Requested', icon: <FaClipboardList />, updatedBy: 'system' },
    { key: 'accepted', label: 'Accepted', icon: <FaCheckCircle />, updatedBy: 'provider' },
    { key: 'reached-site', label: 'Reached Site', icon: <FaMapMarkerAlt />, updatedBy: 'provider' },
    { key: 'in-progress', label: 'Work in Progress', icon: <FaWrench />, updatedBy: 'provider' },
    { key: 'work-completed', label: 'Work Completed', icon: <FaFlagCheckered />, updatedBy: 'provider' },
    { key: 'payment-done', label: 'Payment Done', icon: <FaWallet />, updatedBy: 'customer' },
];

function getStepIndex(status) {
    // work-paused maps to same position as in-progress
    if (status === 'work-paused') return STATUS_STEPS.findIndex(s => s.key === 'in-progress');
    return STATUS_STEPS.findIndex(s => s.key === status);
}

/**
 * StatusTracker — provider view.
 *
 * Props:
 *   currentStatus  – booking's current status
 *   statusHistory  – array of { status, timestamp, note }
 *   onAction       – (nextStatus) => void
 *   role           – 'provider' (always for fixit2)
 *   actionLoading  – boolean
 */
const StatusTracker = ({ currentStatus, statusHistory = [], onAction, role = 'provider', actionLoading }) => {
    const currentIdx = getStepIndex(currentStatus);

    const nextStep = STATUS_STEPS[currentIdx + 1] || null;
    const canAct = nextStep && nextStep.updatedBy === role && onAction;

    const getTimestamp = (key) => {
        const entry = [...statusHistory].reverse().find(h => h.key === key || h.status === key);
        if (!entry) return null;
        const ts = entry.timestamp;
        try {
            return new Date(ts).toLocaleString('en-IN', {
                day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
            });
        } catch {
            return ts;
        }
    };

    const isTerminated = currentStatus === 'cancelled' || currentStatus === 'rejected';
    const isPaused = currentStatus === 'work-paused';

    return (
        <div className="status-tracker">
            <div className="tracker-steps">
                {STATUS_STEPS.map((step, idx) => {
                    const isDone = idx <= currentIdx;
                    const isCurrent = idx === currentIdx;
                    const isFuture = idx > currentIdx;
                    const ts = getTimestamp(step.key);

                    return (
                        <div
                            key={step.key}
                            className={`tracker-step ${isDone ? 'done' : ''} ${isCurrent ? 'current' : ''} ${isFuture ? 'future' : ''} ${isTerminated && isCurrent ? 'terminated' : ''} ${isPaused && step.key === 'in-progress' ? 'paused' : ''}`}
                        >
                            {idx > 0 && (
                                <div className={`tracker-connector ${isDone ? 'filled' : ''}`}></div>
                            )}

                            <div className="tracker-dot">
                                <span className="tracker-icon">{isDone ? step.icon : (idx + 1)}</span>
                            </div>

                            <div className="tracker-label">
                                <span className="tracker-step-name">{step.label}</span>
                                {ts && isDone && <span className="tracker-time">{ts}</span>}
                                {!isDone && (
                                    <span className="tracker-who">
                                        {step.updatedBy === 'customer' ? 'By customer' : step.updatedBy === 'provider' ? 'By you' : ''}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {isTerminated && (
                <div className="tracker-terminated">
                    <span className="terminated-icon">{currentStatus === 'cancelled' ? <FaTimesCircle /> : <FaBan />}</span>
                    <span>Booking {currentStatus}</span>
                </div>
            )}

            {isPaused && (
                <div className="tracker-paused">
                    <FaPause /> Work Paused — will resume next session
                </div>
            )}

            {canAct && !isTerminated && (
                <div className="tracker-action">
                    <button
                        className="tracker-action-btn"
                        onClick={() => onAction(nextStep.key)}
                        disabled={actionLoading}
                    >
                        {actionLoading
                            ? <><FaSpinner className="spin-icon" /> Updating…</>
                            : <>{nextStep.icon} {nextStep.label}</>}
                    </button>
                    <span className="tracker-action-hint">Tap to update status</span>
                </div>
            )}

            {currentStatus === 'payment-done' && (
                <div className="tracker-completed-msg">
                    <FaCheckCircle /> Job completed & payment confirmed!
                </div>
            )}
        </div>
    );
};

export { STATUS_STEPS, getStepIndex };
export default StatusTracker;
